import { useEffect, useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  X,
  Download,
  Plus,
  Trash2,
} from 'lucide-react'
import Card from './Card'
import Button from './Button'
import Input from './Input'
import Textarea from './Textarea'
import Select from './Select'
import {
  PDF_SECTION_OPTIONS,
  DEFAULT_PDF_SECTIONS,
  exportExaminationToPDF,
  formValuesToExamSnapshot,
} from '../services/examinationExport'
import { QUESTION_TYPE_OPTIONS, CHOICE_OPTIONS } from '../utils/constants'

const createQuestion = () => ({
  id: `q_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
  prompt: '',
  type: 'text',
  answer: '',
})

/**
 * Pre-PDF editor: choose sections, edit questions, then download.
 */
const ExaminationPdfEditor = ({
  open,
  onClose,
  formValues = null,
  customQuestions = [],
  examination = null,
  client = null,
  medicalHistory = null,
  blankMode = false,
  title = 'Prepare examination PDF',
}) => {
  const [sections, setSections] = useState([...DEFAULT_PDF_SECTIONS])
  const [questions, setQuestions] = useState([])
  const [blankSlots, setBlankSlots] = useState(blankMode ? 8 : 4)
  const [notes, setNotes] = useState('')
  const [exporting, setExporting] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    if (!open) return
    setSections([...DEFAULT_PDF_SECTIONS])
    setBlankSlots(blankMode ? 8 : 4)
    setError('')

    if (blankMode) {
      setQuestions([])
      setNotes('')
      return
    }

    if (formValues) {
      setNotes(formValues.notes || '')
      setQuestions(
        (customQuestions || [])
          .filter((q) => q?.prompt || q?.answer)
          .map((q) => ({
            id: q.id || createQuestion().id,
            prompt: q.prompt || '',
            type: q.type || 'text',
            answer: q.answer || '',
          }))
      )
      return
    }

    setNotes(examination?.notes || '')
    setQuestions(
      (examination?.custom_questions || []).map((q, index) => ({
        id: `saved_${index}`,
        prompt: q.prompt || '',
        type: q.type || 'text',
        answer: q.answer || '',
      }))
    )
  }, [open, blankMode, formValues, customQuestions, examination])

  const toggleSection = (id) => {
    setSections((prev) =>
      prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id]
    )
  }

  const updateQuestion = (id, field, value) => {
    setQuestions((prev) =>
      prev.map((q) => (q.id === id ? { ...q, [field]: value } : q))
    )
  }

  const removeQuestion = (id) => {
    setQuestions((prev) => prev.filter((q) => q.id !== id))
  }

  const handleDownload = async () => {
    if (sections.length === 0) {
      setError('Select at least one section')
      return
    }

    try {
      setExporting(true)
      setError('')

      const pdfOptions = {
        sections,
        blankQuestionnaireSlots: Number(blankSlots) || 0,
        title: blankMode ? 'NaturAssist Examination Form' : 'NaturAssist Examination Record',
      }

      if (blankMode) {
        await exportExaminationToPDF({}, {}, {}, pdfOptions)
      } else if (formValues) {
        const snapshot = formValuesToExamSnapshot(
          { ...formValues, notes },
          questions
        )
        await exportExaminationToPDF(
          snapshot.examination,
          snapshot.client,
          snapshot.medicalHistory,
          pdfOptions
        )
      } else {
        await exportExaminationToPDF(
          {
            ...(examination || {}),
            notes,
            custom_questions: questions,
          },
          client || {},
          medicalHistory || examination?.medical_history || {},
          pdfOptions
        )
      }

      onClose?.()
    } catch (err) {
      setError(err.message || 'Failed to create PDF')
    } finally {
      setExporting(false)
    }
  }

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
        >
          <motion.div
            initial={{ opacity: 0, y: 16, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 16, scale: 0.98 }}
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-3xl max-h-[90vh] overflow-y-auto"
          >
            <Card variant="soft" className="p-5 sm:p-6">
              <div className="flex items-start justify-between gap-3 mb-4">
                <div>
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white">{title}</h3>
                  <p className="text-sm text-gray-500 mt-1">
                    Choose sections and edit questions before downloading.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={onClose}
                  className="p-2 rounded-full bg-gray-100 text-gray-500 hover:text-gray-700"
                >
                  <X size={18} />
                </button>
              </div>

              {error && (
                <div className="mb-4 p-3 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm">
                  {error}
                </div>
              )}

              <div className="space-y-5">
                <section>
                  <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-200 mb-2">
                    Sections to include
                  </h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {PDF_SECTION_OPTIONS.map((section) => (
                      <label
                        key={section.id}
                        className="flex items-center gap-2 p-2 rounded-lg border border-gray-200 dark:border-gray-700 cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={sections.includes(section.id)}
                          onChange={() => toggleSection(section.id)}
                        />
                        <span className="text-sm text-gray-800 dark:text-gray-100">
                          {section.label}
                        </span>
                      </label>
                    ))}
                  </div>
                  <div className="flex gap-2 mt-2">
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      onClick={() => setSections([...DEFAULT_PDF_SECTIONS])}
                    >
                      Select all
                    </Button>
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      onClick={() => setSections([])}
                    >
                      Clear
                    </Button>
                  </div>
                </section>

                <section>
                  <Input
                    label="Blank question slots"
                    type="number"
                    min="0"
                    max="20"
                    value={blankSlots}
                    onChange={(e) => setBlankSlots(e.target.value)}
                  />
                </section>

                {sections.includes('questionnaire') && (
                  <section className="space-y-3">
                    <div className="flex items-center justify-between gap-2">
                      <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-200">
                        Questions (editable for this PDF)
                      </h4>
                      <Button
                        type="button"
                        variant="secondary"
                        size="sm"
                        className="flex items-center gap-1"
                        onClick={() => setQuestions((prev) => [...prev, createQuestion()])}
                      >
                        <Plus size={14} />
                        Add
                      </Button>
                    </div>

                    {questions.length === 0 && (
                      <p className="text-sm text-gray-500">
                        No questions yet. Add some, or rely on blank slots.
                      </p>
                    )}

                    {questions.map((question, index) => (
                      <div
                        key={question.id}
                        className="p-3 rounded-xl border border-gray-200 dark:border-gray-700 space-y-2"
                      >
                        <div className="flex items-center justify-between">
                          <p className="text-xs font-semibold text-gray-500">Q{index + 1}</p>
                          <button
                            type="button"
                            onClick={() => removeQuestion(question.id)}
                            className="p-1 text-red-500 hover:bg-red-50 rounded"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                        <Input
                          label="Prompt"
                          value={question.prompt}
                          onChange={(e) => updateQuestion(question.id, 'prompt', e.target.value)}
                          placeholder="Question text"
                        />
                        <Select
                          label="Type"
                          value={question.type}
                          onChange={(e) => updateQuestion(question.id, 'type', e.target.value)}
                          options={QUESTION_TYPE_OPTIONS}
                        />
                        {question.type === 'choice' ? (
                          <Select
                            label="Answer"
                            value={question.answer}
                            onChange={(e) => updateQuestion(question.id, 'answer', e.target.value)}
                            options={[
                              { value: '', label: 'Leave blank (write-in)' },
                              ...CHOICE_OPTIONS.map((opt) => ({ value: opt, label: opt })),
                            ]}
                          />
                        ) : (
                          <Textarea
                            label="Answer"
                            value={question.answer}
                            onChange={(e) => updateQuestion(question.id, 'answer', e.target.value)}
                            placeholder="Leave blank for handwriting lines"
                          />
                        )}
                      </div>
                    ))}
                  </section>
                )}

                {sections.includes('consent') && (
                  <Textarea
                    label="Additional clinical notes (optional)"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                  />
                )}

                <div className="flex flex-wrap justify-end gap-2 pt-2">
                  <Button type="button" variant="secondary" onClick={onClose}>
                    Cancel
                  </Button>
                  <Button
                    type="button"
                    onClick={handleDownload}
                    disabled={exporting}
                    className="flex items-center gap-2"
                  >
                    <Download size={16} />
                    {exporting ? 'Creating PDF…' : 'Download PDF'}
                  </Button>
                </div>
              </div>
            </Card>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}

export default ExaminationPdfEditor
